import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { IPSAuthenticationService } from '@ips/common-angular';

const orderDetailsReturnUrlKey = 'orderDetailsReturnUrl';
const orderDetailsPath = /^\/home\/order-details\/[^/?#]+$/;

export const orderDetailsRedirect: CanActivateFn = (route) => {
  const platformId = inject(PLATFORM_ID);
  const document = inject(DOCUMENT);
  const router = inject(Router);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const storage = document.defaultView!.sessionStorage;
  const returnUrl = storage.getItem(orderDetailsReturnUrlKey);
  if (!returnUrl) {
    return true;
  }
  if (!orderDetailsPath.test(returnUrl)) {
    console.warn('Ignoring invalid saved order details destination.');
    storage.removeItem(orderDetailsReturnUrlKey);
    return true;
  }
  if (route.queryParamMap.has('returnUrl')) {
    return true;
  }
  // Restore before landing activation: its authentication subscription may emit synchronously.
  return router.createUrlTree(['/'], {
    queryParams: { ...route.queryParams, returnUrl: encodeURIComponent(returnUrl) },
    fragment: route.fragment ?? undefined
  });
};

export const authorizationGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const platformId = inject(PLATFORM_ID);
  const authService = inject(IPSAuthenticationService);
  const router = inject(Router);
  const document = inject(DOCUMENT);

  if (isPlatformBrowser(platformId)) {
    const storage = document.defaultView!.sessionStorage;
    const currentUser = authService.currentUserValue;
    if (currentUser?.authenticated) {
      storage.removeItem(orderDetailsReturnUrlKey);
      // authorised so return true
      return true;
    } else {
      // OAuth callbacks can drop query parameters, so retain the order in this tab until login completes.
      if (orderDetailsPath.test(state.url)) {
        storage.setItem(orderDetailsReturnUrlKey, state.url);
      } else {
        storage.removeItem(orderDetailsReturnUrlKey);
      }
      // not logged in so redirect to login page with the return url
      router.navigate([''], { queryParams: { returnUrl: encodeURIComponent(state.url) } });
      return false;
    }
  }

  // If not running in the browser, allow the navigation
  return true;
};
 